import { z } from 'zod';
import { ADVISOR_FIRM_SLUG_PATTERN } from './advisorFirmDirectory.js';
import { ADVISOR_CLIENT_EMAIL_MAX } from './advisorClient.js';
import { ULID_PATTERN } from './intakeMessage.js';
import {
  INTAKE_SNAPSHOT_MAX_ACCOUNTS, INTAKE_SNAPSHOT_MAX_HOLDINGS, INTAKE_SNAPSHOT_MAX_INSTITUTIONS,
  IntakeDraftSnapshotSchema, type IntakeDraftSnapshot,
} from './intakeSnapshot.js';

/*
 * The `link-connected` writer message and the pure pieces of the writer-owned `client-connected` row
 * (docs/43 §3.2, §5.1, slice 4d-writer; crossmarc/steward-app#2699).
 *
 * The façade enqueues one `link-connected` right after kept-link answers a link, carrying kept-link's
 * returned snapshot (after the façade's own second debt filter) and NOTHING from the request. The writer
 * merges it into the client's connected row (`mergeLinkSnapshot`, the server-side port of the SPA's
 * `mergeSnapshot`) and replaces the row with a `_v`-conditional PUT.
 *
 * 🚨 No `clientId`, no `by`, no `draftId` key: the writer takes the client from its own owner row and
 * slot (`bindLogin`), and `by` is `userId`, the signed-in caller the façade resolved from the caller's
 * own token. The strict schema refuses any of them.
 */

export const LINK_CONNECTED_KIND = 'link-connected';

export const ClientLinkConnectedMessageSchema = z.strictObject({
  kind: z.literal(LINK_CONNECTED_KIND),
  v: z.literal(1),
  firmId: z.string().regex(ADVISOR_FIRM_SLUG_PATTERN),
  userId: z.string().min(1).max(200).regex(/^[^\s#]+$/),
  /** The permit id the same request consumed (kept-link's `keptId`), the per-link no-op key. */
  /** The signed-in caller's address, as `open` carries it: `bindLogin` uses it only when it hashes to the owner's proof. */
  email: z.string().min(3).max(ADVISOR_CLIENT_EMAIL_MAX),
  linkId: z.string().regex(ULID_PATTERN),
  savedAt: z.iso.datetime().max(64),
  snapshot: IntakeDraftSnapshotSchema,
});
export type ClientLinkConnectedMessage = z.infer<typeof ClientLinkConnectedMessageSchema>;

export function parseClientLinkConnectedMessage(value: unknown): ClientLinkConnectedMessage | undefined {
  const parsed = ClientLinkConnectedMessageSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}

/** The façade's gate: a snapshot with no account and no holding is not worth a message. */
export function snapshotHasContent(snapshot: unknown): boolean {
  if (typeof snapshot !== 'object' || snapshot === null) return false;
  const s = snapshot as Record<string, unknown>;
  return (Array.isArray(s['accounts']) && s['accounts'].length > 0)
    || (Array.isArray(s['holdings']) && s['holdings'].length > 0);
}

const asArray = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

/**
 * One link's snapshot merged into the stored one (the SPA's `mergeSnapshot`, moved server-side). A stored
 * account is superseded by an incoming one with the same id or the same institution name; the holdings of a
 * superseded account go with it. INCOMING FIRST, so a cap trims the OLDEST data, never the link the person
 * just made. Idempotent for the same incoming value. Pure: tombstones and the debt rule are applied by the
 * caller afterwards.
 */
export function mergeLinkSnapshot(
  stored: Record<string, unknown> | undefined, incoming: Record<string, unknown>,
): Record<string, unknown> {
  if (!stored) return incoming;
  const incomingAccounts = asArray(incoming['accounts']);
  const replacedIds = new Set(
    incomingAccounts.map((a) => (a as { accountId?: unknown }).accountId).filter((id): id is string => typeof id === 'string'),
  );
  const replacedInstitutions = new Set(
    incomingAccounts.map((a) => (a as { institution?: unknown }).institution)
      .filter((name): name is string => typeof name === 'string' && name !== ''),
  );
  const supersededIds = new Set<string>();
  const survivingAccounts = asArray(stored['accounts']).filter((entry) => {
    const account = entry as { accountId?: unknown; institution?: unknown };
    const id = typeof account.accountId === 'string' ? account.accountId : '';
    const institution = typeof account.institution === 'string' ? account.institution : '';
    const superseded = (id !== '' && replacedIds.has(id)) || (institution !== '' && replacedInstitutions.has(institution));
    if (superseded && id !== '') supersededIds.add(id);
    return !superseded;
  });
  const survivingHoldings = asArray(stored['holdings']).filter((entry) => {
    const id = (entry as { accountId?: unknown }).accountId;
    return typeof id === 'string' && !supersededIds.has(id);
  });
  const institutions = [
    ...asArray(stored['institutions']).filter((n): n is string => typeof n === 'string'),
    ...asArray(incoming['institutions']).filter((n): n is string => typeof n === 'string'),
  ];
  const truncated = incoming['holdingsTruncated'] === true || stored['holdingsTruncated'] === true;
  return {
    ...incoming,
    institutions: [...new Set(institutions)].slice(0, INTAKE_SNAPSHOT_MAX_INSTITUTIONS),
    accounts: [...incomingAccounts, ...survivingAccounts].slice(0, INTAKE_SNAPSHOT_MAX_ACCOUNTS),
    holdings: [...asArray(incoming['holdings']), ...survivingHoldings].slice(0, INTAKE_SNAPSHOT_MAX_HOLDINGS),
    ...(truncated ? { holdingsTruncated: true } : {}),
  };
}

/**
 * The value a removal that empties the snapshot stores. The appcore row has no DELETE, so "nothing
 * connected" is a snapshot with empty arrays (decided with the coordinator, 2026-11-01). Readers treat a
 * snapshot with no account and no holding as none.
 */
export function emptyConnectedSnapshot(asOf: string): IntakeDraftSnapshot {
  return { asOf, provider: 'plaid', institutions: [], accounts: [], holdings: [] };
}

// ---------------------------------------------------------------------------------------------
// appcore's bounds on the row (client-record-rows-system.ts on appcore main 08552a9, read 2026-11-01)
// ---------------------------------------------------------------------------------------------

/** appcore's `CLIENT_RECORD_ROW_MAX_ATTRIBUTE_BYTES`: marshalled bytes of the attributes map above which a PUT is 422. */
export const CLIENT_RECORD_ROW_MAX_ATTRIBUTE_BYTES = 380_000;
/** appcore's `CLIENT_DOCUMENTS_MAX`. */
export const CLIENT_RECORD_DOCUMENTS_MAX = 20;

/**
 * A copy of appcore's `marshalledSize` (DynamoDB's documented sizing: UTF-8 bytes for a string, about
 * digits/2 + 1 for a number, 1 for a boolean or null, 3 + 1 per element + the elements for a list, a map
 * also counting its keys). The writer cannot import appcore, so the size test measures with this copy; if
 * appcore's changes, this one must follow.
 */
export function marshalledSize(value: unknown): number {
  const utf8 = (s: string): number => new TextEncoder().encode(s).length;
  if (typeof value === 'string') return utf8(value);
  if (typeof value === 'number') {
    const digits = String(Math.abs(value)).replace(/e.*$/i, '').replace('.', '').replace(/^0+/, '').replace(/0+$/, '').length;
    return Math.ceil(digits / 2) + 1 + (value < 0 ? 1 : 0);
  }
  if (typeof value !== 'object' || value === null) return 1;
  if (Array.isArray(value)) return value.reduce<number>((n, v) => n + 1 + marshalledSize(v), 3);
  return Object.entries(value).reduce((n, [k, v]) => n + 1 + utf8(k) + marshalledSize(v), 3);
}

/** A character that is one UTF-16 unit and three UTF-8 bytes: a text field "at its cap" costs 3 bytes per unit. */
const SNOWMAN = '☃';

/**
 * The largest snapshot `IntakeDraftSnapshotSchema` admits: 10 institutions, 50 accounts and 100 holdings,
 * every optional field present and every text field at its cap in 3-byte characters, the longest numbers.
 * `clientConnected.test.ts` asserts the schema ACCEPTS it, so the caps counted are the caps enforced.
 */
export function worstCaseConnectedSnapshot(): IntakeDraftSnapshot {
  const text = SNOWMAN.repeat(120);
  const id = 'A'.repeat(64);
  const amount = -0.12345678901234567;
  return {
    asOf: '2026-10-31T23:59:59.999Z',
    provider: 'plaid',
    institutions: Array.from({ length: INTAKE_SNAPSHOT_MAX_INSTITUTIONS }, () => text),
    accounts: Array.from({ length: INTAKE_SNAPSHOT_MAX_ACCOUNTS }, () => ({
      accountId: id, institution: text, name: text, mask: 'A'.repeat(8), type: 'a'.repeat(32),
      subtype: 'a'.repeat(48), currency: 'USD', balance: amount, available: amount, statementsUnavailable: true,
    })),
    holdings: Array.from({ length: INTAKE_SNAPSHOT_MAX_HOLDINGS }, () => ({
      accountId: id, name: text, symbol: SNOWMAN.repeat(24), quantity: amount, value: amount, currency: 'USD',
    })),
    holdingsTruncated: true,
  } as IntakeDraftSnapshot;
}

/** The whole `client-connected` attributes map as appcore stores it: `{ _v, via, by, at, snapshot }`. */
export function worstCaseConnectedRowAttributes(): Record<string, unknown> {
  return {
    _v: 2_147_483_647, via: 'connected', by: '0'.repeat(26), at: '2026-10-31T23:59:59.999Z',
    snapshot: worstCaseConnectedSnapshot(),
  };
}
